import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agentApi, LoginCredentials, VerifyCodeRequest } from '../api/agent.api';
import { useAgentStore } from '../store/agentStore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useState } from 'react';

export const useAgentAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAuth, logout: logoutStore, isAuthenticated } = useAgentStore();
  
  // Estado para el flujo 2FA
  const [requiresCode, setRequiresCode] = useState(false);
  const [username, setUsername] = useState(''); // Username del agente para verificación
  const [partialEmail, setPartialEmail] = useState(''); // Email parcialmente oculto para mostrar en UI

  // Mutation para login (Paso 1)
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => {
      return agentApi.login(credentials);
    },
    onSuccess: (data, variables) => {
      // Verificar si requiere código 2FA
      if (agentApi.requires2FA(data)) {
        setRequiresCode(true);
        setPartialEmail(data.email); // Email parcialmente oculto para mostrar
        // Guardar el username para usar en la verificación del código
        setUsername(variables.username);
        toast.success(data.message);
        return;
      }

      // Si no requiere 2FA (caso legacy), procesar directamente
      processSuccessfulLogin(data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al iniciar sesión');
    },
  });

  // Mutation para verificar código (Paso 2)
  const verifyCodeMutation = useMutation({
    mutationFn: (request: VerifyCodeRequest) => {
      return agentApi.verifyLoginCode(request);
    },
    onSuccess: (data) => {
      processSuccessfulLogin(data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Código inválido');
    },
  });

  // Procesar login exitoso (compartido entre ambos flujos)
  const processSuccessfulLogin = (data: any) => {
    // Validar que el usuario tenga rol de AGENT (soporta multi-rol)
    const hasAgentRole = data.user?.roles?.includes('AGENT') || data.user?.role === 'AGENT';
    if (!hasAgentRole) {
      toast.error('Error: Este usuario no es un agente. Por favor use credenciales de agente.');
      return;
    }
    
    // Guardar datos del usuario y token
    // (socket connection is managed by useAgentNotifications in App.tsx)
    setAuth(data.accessToken, data.user);
    queryClient.setQueryData(['agent-profile'], data.user);
    
    // Resetear estado 2FA
    setRequiresCode(false);
    setUsername('');
    setPartialEmail('');
    
    toast.success(`Bienvenido, ${data.user.firstName} ${data.user.lastName}`);
    navigate('/');
  };

  const logout = () => {
    logoutStore();
    queryClient.clear();
    setRequiresCode(false);
    setUsername('');
    setPartialEmail('');
    navigate('/login');
    toast.success('Sesión cerrada');
  };

  const resetFlow = () => {
    setRequiresCode(false);
    setUsername('');
    setPartialEmail('');
  };

  return {
    // Paso 1: Login
    login: (credentials: LoginCredentials) => {
      loginMutation.mutate(credentials);
    },
    isLoggingIn: loginMutation.isPending,
    
    // Paso 2: Verificar código
    verifyCode: (code: string) => {
      verifyCodeMutation.mutate({ username, code });
    },
    isVerifyingCode: verifyCodeMutation.isPending,
    
    // Estado 2FA
    requiresCode,
    partialEmail,
    resetFlow,
    
    // Otros
    logout,
    isAuthenticated,
  };
};
